import { Button, type ButtonProps, styled } from '@mui/material';
import { useConfirmationDialog } from '@tempo-io/roadmapping-frontend-confirm-dialog';
import {
  AccountTree,
  Code,
  ControlPointDuplicate,
  Delete,
  Edit,
  OpenInFull,
  SwapCalls,
  TrendingUp,
} from '@tempo-io/sui-icons';
import type { ReactNode, SyntheticEvent } from 'react';

import { useSwimlaneEventPublisher } from '@/components/ui/swimlane/api';
import type { ICardProps, IItemCardActions } from '@/components/ui/swimlane/types';

export type SLCardContextMenuOption = {
  label: string;
  icon?: ReactNode;
  isDisabled?: boolean;
  isHidden?: boolean;
  action: (event: SyntheticEvent<HTMLElement>) => void;
};

export type SLCardContextMenuSlotData = {
  slot: string;
  menuOptions: SLCardContextMenuOption[];
};

export interface IUseSLCardContextMenuSlots extends ICardProps {
  canEdit: boolean; // TODO: implement as per other permission/read-only checks; maybe use hook
  isDeletable?: boolean; // linked integration items are not deletable RMK-6259: implement isDeletable
  isPublicationMode?: boolean;
  itemCardActions?: IItemCardActions;
  isSubItemFeaturesEnabled: boolean;
}

export const SLCardContextMenuOptionTypes = {
  OPEN_ITEM_CARD: 'openItemCard',
  COPY_ITEM_URL: 'copyItemURL',
  ADD_SUB_ITEM: 'addSubItem',
  ADD_LINKED_ITEM: 'addLinkedItem',
  RENAME_ITEM: 'renameItem',
  TRANSFER_ITEM: 'transferItem',
  DUPLICATE_ITEM: 'duplicateItem',
  DELETE_ITEM: 'deleteItem',
} as const;

export type SLCardContextMenuOptionTypesValue =
  (typeof SLCardContextMenuOptionTypes)[keyof typeof SLCardContextMenuOptionTypes];

export const SL_CARD_CONTEXT_MENU_OPTION_ICONS = {
  [SLCardContextMenuOptionTypes.OPEN_ITEM_CARD]: <OpenInFull />,
  [SLCardContextMenuOptionTypes.COPY_ITEM_URL]: <Code />,
  [SLCardContextMenuOptionTypes.ADD_SUB_ITEM]: <AccountTree />,
  [SLCardContextMenuOptionTypes.ADD_LINKED_ITEM]: <SwapCalls />,
  [SLCardContextMenuOptionTypes.RENAME_ITEM]: <Edit />,
  [SLCardContextMenuOptionTypes.TRANSFER_ITEM]: <TrendingUp />,
  [SLCardContextMenuOptionTypes.DUPLICATE_ITEM]: <ControlPointDuplicate />,
  [SLCardContextMenuOptionTypes.DELETE_ITEM]: <Delete />,
};

export default function useSLCardContextMenuSlots({
  itemID,
  columnID,
  rowID,
  itemCardActions,
  canEdit,
  isDeletable,
  isPublicationMode = false,
  isSubItemFeaturesEnabled = false,
}: IUseSLCardContextMenuSlots): SLCardContextMenuSlotData[] {
  const { openDialog, closeDialog } = useConfirmationDialog();
  const publishEvent = useSwimlaneEventPublisher();

  if (!itemID) {
    return [];
  }

  const DialogActions = (
    <>
      <CancelButton onClick={closeDialog}>Cancel</CancelButton>
      <DeleteItemButton
        color="error"
        onClick={() => {
          itemCardActions?.deleteItemHandler(itemID);
          closeDialog();
        }}
      >
        Delete Item
      </DeleteItemButton>
    </>
  );

  const deleteItemHandlerWithConfirmation = () => {
    openDialog({
      title: 'Delete Item',
      content: 'Are you certain you wish to delete this item? This action cannot be undone.',
      showCloseButton: true,
      actionsSlot: DialogActions,
      onDialogClose: closeDialog,
    });
  };

  const secondSectionMenuOptions = [
    {
      label: 'Add Linked Item',
      isDisabled: !canEdit,
      isHidden: isPublicationMode,
      icon: SL_CARD_CONTEXT_MENU_OPTION_ICONS[SLCardContextMenuOptionTypes.ADD_LINKED_ITEM],
      action: () => itemCardActions?.addLinkedItemHandler(itemID),
    },
  ];

  if (isSubItemFeaturesEnabled) {
    secondSectionMenuOptions.unshift({
      label: 'Add Sub Item',
      isDisabled: !canEdit,
      isHidden: isPublicationMode,
      icon: SL_CARD_CONTEXT_MENU_OPTION_ICONS[SLCardContextMenuOptionTypes.ADD_SUB_ITEM],
      action: () => itemCardActions?.addSubItemHandler(itemID),
    });
  }

  // Temporarily hide options (plan to re-enable later)
  // @ts-expect-error - Keeping for future re-enablement
  const _menuSlots = [
    {
      slot: 'first-section',
      menuOptions: [
        {
          label: 'Open Item Card',
          isDisabled: false, // always enabled, even in read-only mode
          icon: SL_CARD_CONTEXT_MENU_OPTION_ICONS[SLCardContextMenuOptionTypes.OPEN_ITEM_CARD],
          action: (event: SyntheticEvent<HTMLElement, Event>) =>
            publishEvent('openCardDetails', { itemID, columnID, rowID }, event),
        },
        {
          label: 'Copy Item URL',
          isDisabled: false, // always enabled, even in read-only mode
          isHidden: isPublicationMode, // TODO: RMK-8400 Implement the Copy Item URL feature in a publication
          icon: SL_CARD_CONTEXT_MENU_OPTION_ICONS[SLCardContextMenuOptionTypes.COPY_ITEM_URL],
          action: () => itemCardActions?.copyItemURLHandler(itemID),
        },
      ],
    },
    {
      slot: 'second-section',
      menuOptions: secondSectionMenuOptions,
    },
    {
      slot: 'third-section',
      menuOptions: [
        {
          label: 'Rename Item',
          isDisabled: !canEdit,
          isHidden: isPublicationMode,
          icon: SL_CARD_CONTEXT_MENU_OPTION_ICONS[SLCardContextMenuOptionTypes.RENAME_ITEM],
          action: () => itemCardActions?.renameItemHandler(itemID),
        },
        {
          label: 'Transfer Item',
          isDisabled: !canEdit,
          isHidden: isPublicationMode,
          icon: SL_CARD_CONTEXT_MENU_OPTION_ICONS[SLCardContextMenuOptionTypes.TRANSFER_ITEM],
          action: () => itemCardActions?.transferItemHandler(itemID),
        },
        {
          label: 'Duplicate Item',
          isDisabled: !canEdit,
          isHidden: isPublicationMode,
          icon: SL_CARD_CONTEXT_MENU_OPTION_ICONS[SLCardContextMenuOptionTypes.DUPLICATE_ITEM],
          action: () => itemCardActions?.duplicateItemHandler(itemID),
        },
      ],
    },
    {
      slot: 'fourth-section',
      menuOptions: [
        {
          label: 'Delete Item',
          isDisabled: !canEdit || !isDeletable,
          isHidden: isPublicationMode,
          icon: SL_CARD_CONTEXT_MENU_OPTION_ICONS[SLCardContextMenuOptionTypes.DELETE_ITEM],
          action: () => deleteItemHandlerWithConfirmation(),
        },
      ],
    },
  ].filter((slot) => {
    slot.menuOptions = slot.menuOptions.filter((option) => !option.isHidden);
    return slot.menuOptions.length > 0;
  });

  return [
    {
      slot: 'first-section',
      menuOptions: [
        {
          label: 'Open Item Card',
          isDisabled: false, // always enabled, even in read-only mode
          icon: SL_CARD_CONTEXT_MENU_OPTION_ICONS[SLCardContextMenuOptionTypes.OPEN_ITEM_CARD],
          action: (event: SyntheticEvent<HTMLElement, Event>) =>
            publishEvent('openCardDetails', { itemID, columnID, rowID }, event),
        },
      ],
    },
  ];
}

const CancelButton = styled(Button)<ButtonProps>(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const DeleteItemButton = styled(Button)<ButtonProps>(({ theme }) => ({
  color: theme.palette.getContrastText(theme.palette.error.main),
  backgroundColor: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.error.main,
  },
}));
